/**
 * batch-66 interne-geldstroom (FO-494) - de pure kern van het koppelen van een
 * inkoopfactuur aan de interne geldstroom (BV -> BV).
 *
 * Geen Prisma, geen `node:*`: alles hier is een functie van zijn argumenten, zodat
 * de API, het opschoonscript en de tests exact dezelfde regels gebruiken.
 *
 *   - `stelTegenpartijVoor`        welke eigen BV heeft deze factuur gestuurd?
 *   - `stelSpiegelVoor`            welke verkoopfactuur bij die BV is de spiegel
 *                                  (nummer / bedrag / datum plus of min 3 dagen)?
 *   - `stelEngineTransactiesVoor`  welke niet-afgerekende doorbelastingen horen erbij?
 *   - `stelOrigineelVoor`          bij een creditnota: welke geldstroom wordt gecrediteerd?
 *   - `beoordeelInterneGeldstroom` de status (OPEN / GEKOPPELD / AFGEREKEND / VERSCHIL)
 *                                  uit de feiten; een verschil is nooit stil.
 *   - `beoordeelAutomatischKoppelen` mag de bulk/opschoning dit paar zelf koppelen?
 *
 * Geld: hele centen (integers). De API zet `Prisma.Decimal` om via
 * `decimalNaarCenten`; hier wordt nooit met floats over geld gerekend.
 * Datums: kalenderdagen in Europe/Amsterdam.
 */

import {
  INTERNE_GELDSTROOM_KOPPELBARE_INKOOP_STATUSSEN,
  INTERNE_SPIEGEL_DATUM_TOLERANTIE_DAGEN,
  type InterneGeldstroomBlokkade,
  type InterneGeldstroomHandmatigReden,
  type InterneGeldstroomOorsprong,
  type InterneGeldstroomStatus,
  type InterneGeldstroomVerschilReden,
  type InterneSpiegelSterkte,
  type InterneTegenpartijBron,
} from '@streep/types'
import { utcToAmsterdamWallclock } from './amsterdam-tijd'
import { herkenEigenBv, type EigenBvKandidaat } from './eigen-bv-herkenning'
import { kwartaalRange, maandRange, weekRange } from './intercompany-periode'

// Korte aliassen op de gedeelde unions uit `@streep/types` (één bron).
export type GeldstroomStatus = InterneGeldstroomStatus
export type GeldstroomVerschilReden = InterneGeldstroomVerschilReden
export type GeldstroomOorsprong = InterneGeldstroomOorsprong
export type SpiegelSterkte = InterneSpiegelSterkte
export type TegenpartijBron = InterneTegenpartijBron
export type GeldstroomHandmatigReden = InterneGeldstroomHandmatigReden

/** Dagen dat de spiegel-verkoopfactuur van de factuurdatum mag afwijken. */
export const SPIEGEL_DATUM_TOLERANTIE_DAGEN = INTERNE_SPIEGEL_DATUM_TOLERANTIE_DAGEN

const DAG_MS = 86_400_000

// ─── Periode (maand, Europe/Amsterdam) ───────────────────────────────────────

const PERIODE_RE = /^(\d{4})-(0[1-9]|1[0-2])$/

/** Is dit een geldige maandperiode `YYYY-MM`? */
export function isGeldstroomPeriode(periode: string | null | undefined): periode is string {
  return typeof periode === 'string' && PERIODE_RE.test(periode)
}

/** De maandperiode (`YYYY-MM`) van een instant, in Europe/Amsterdam. */
export function geldstroomPeriodeVanDatum(d: Date): string {
  return utcToAmsterdamWallclock(d).ymd.slice(0, 7)
}

/** De maand vóór een periode (`2026-01` -> `2025-12`). */
export function vorigeGeldstroomPeriode(periode: string): string {
  const m = PERIODE_RE.exec(periode)
  if (!m) return periode
  const totaal = Number(m[1]) * 12 + (Number(m[2]) - 1) - 1
  return `${Math.floor(totaal / 12)}-${String((totaal % 12) + 1).padStart(2, '0')}`
}

const MAANDEN = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december',
] as const

/** Leesbaar label van een maandperiode ("september 2026"). */
export function geldstroomPeriodeLabel(periode: string): string {
  const m = PERIODE_RE.exec(periode)
  if (!m) return periode
  return `${MAANDEN[Number(m[2]) - 1]} ${m[1]}`
}

/**
 * De standaardperiode van een interne factuur: de periode van de herhaal-regel
 * (alleen de maandvorm `YYYY-MM`), anders de maand van de factuurdatum. De
 * gebruiker kan hem bij het koppelen verzetten (een factuur van 1 oktober gaat
 * meestal over september).
 */
export function bepaalGeldstroomPeriode(invoer: {
  factuurdatum: Date
  herhaalPeriode?: string | null
}): string {
  if (isGeldstroomPeriode(invoer.herhaalPeriode)) return invoer.herhaalPeriode
  return geldstroomPeriodeVanDatum(invoer.factuurdatum)
}

// ─── Engine-periode <-> maand ────────────────────────────────────────────────

export interface EnginePeriodeInfo {
  periodetype: 'MAAND' | 'WEEK' | 'KWARTAAL'
  jaar: number
  maand: number | null
  week: number | null
  kwartaal: number | null
  periodeVan?: Date | null
  periodeTot?: Date | null
}

/** Het werkelijke tijdvak van een engine-periode (UTC-instants van NL-grenzen). */
export function enginePeriodeRange(p: EnginePeriodeInfo): { van: Date; tot: Date } | null {
  if (p.periodeVan && p.periodeTot) return { van: p.periodeVan, tot: p.periodeTot }
  if (p.periodetype === 'MAAND' && p.maand != null) return maandRange(p.jaar, p.maand)
  if (p.periodetype === 'WEEK' && p.week != null) return weekRange(p.jaar, p.week)
  if (p.periodetype === 'KWARTAAL' && p.kwartaal != null) return kwartaalRange(p.jaar, p.kwartaal)
  return null
}

/**
 * Overlapt een engine-periode de maand van de geldstroom? Voor het VOORSTEL:
 * een maandperiode overlapt alleen zichzelf, een kwartaal zijn drie maanden en
 * een week de maand(en) waar hij in valt. Ruim, want de gebruiker bevestigt; een
 * eenmaal afgerekende doorbelasting verdwijnt daarna uit elk ander voorstel.
 */
export function enginePeriodeOverlaptMaand(p: EnginePeriodeInfo, periode: string): boolean {
  const m = PERIODE_RE.exec(periode)
  if (!m) return false
  const range = enginePeriodeRange(p)
  if (!range) return false
  const maand = maandRange(Number(m[1]), Number(m[2]))
  return range.van.getTime() <= maand.tot.getTime() && range.tot.getTime() >= maand.van.getTime()
}

/**
 * De ENE maand waar een engine-periode in het periodeoverzicht onder valt, zodat
 * geen bedrag dubbel telt: een maand is zichzelf, een kwartaal zijn laatste
 * maand (daar wordt afgerekend), een week de maand van zijn donderdag (ISO-8601:
 * de week hoort bij het jaar/de maand van de donderdag).
 */
export function enginePeriodeHoofdmaand(p: EnginePeriodeInfo): string | null {
  if (p.periodetype === 'MAAND') {
    return p.maand == null ? null : `${p.jaar}-${String(p.maand).padStart(2, '0')}`
  }
  if (p.periodetype === 'KWARTAAL') {
    return p.kwartaal == null ? null : `${p.jaar}-${String(p.kwartaal * 3).padStart(2, '0')}`
  }
  const range = enginePeriodeRange(p)
  if (!range) return null
  // Maandag 00:00 NL + 3 dagen + 12 uur = donderdagmiddag NL (DST-bestendig).
  return geldstroomPeriodeVanDatum(new Date(range.van.getTime() + 3 * DAG_MS + DAG_MS / 2))
}

/**
 * Engine-statussen met een BEREKEND bedrag: alleen die zijn af te rekenen met een
 * factuur en tellen mee in het engine-saldo. Zonder bedrag (te berekenen,
 * wachtstanden, geblokkeerd) valt er niets te vergelijken; afgekeurd en
 * geannuleerd tellen niet mee.
 *
 * GECORRIGEERD telt WEL mee. Een correctie in de engine laat het origineel staan
 * (status GECORRIGEERD) en boekt er een credit (min het oude bedrag) en een
 * nieuwe debet (het nieuwe bedrag) naast. Alle drie samen geven het juiste
 * saldo: 5.200 - 5.200 + 5.000 = 5.000. Zou het origineel wegvallen, dan stond
 * de credit er los tegenover (saldo -200) en bleef hij voor altijd "niet
 * afgerekend". Zo blijft een eerder afgerekende factuur van 5.200 aan zijn
 * origineel hangen en rekent de creditnota van -200 af met credit plus debet.
 */
export const ENGINE_AFREKENBARE_STATUSSEN = [
  'BEREKEND',
  'TE_KEUREN',
  'GOEDGEKEURD',
  'GEFACTUREERD',
  'BETAALD',
  'GECORRIGEERD',
] as const

/** Engine-statussen waarin de doorbelasting nog geen bedrag heeft. */
export const ENGINE_NOG_NIET_BEREKENDE_STATUSSEN = [
  'TE_BEREKENEN',
  'WACHT_OP_LEASEPRIJS',
  'WACHT_OP_OMZETCIJFERS',
  'WACHT_OP_GOEDKEURING_UREN',
  'WACHT_OP_VERDEELSLEUTEL',
  'UREN_GOEDGEKEURD',
  'GEBLOKKEERD',
] as const

export function isAfrekenbareEngineStatus(status: string): boolean {
  return (ENGINE_AFREKENBARE_STATUSSEN as readonly string[]).includes(status)
}

// ─── Tegenpartij ─────────────────────────────────────────────────────────────

export interface TegenpartijVoorstel<T extends EigenBvKandidaat> {
  entity: T | null
  bron: TegenpartijBron | null
  /** De herkende BV is dezelfde als de ontvangende BV (een BV factureert zichzelf niet). */
  zelfdeBv: boolean
}

/**
 * Welke eigen BV heeft deze inkoopfactuur gestuurd? Volgorde van zekerheid:
 *   1. de bestaande intercompany-markering (`intercompanyTegenpartijEntityId`);
 *   2. de leverancier IS de interne relatie van een BV (`Client.type = INTERN`);
 *   3. de strikte eigen-BV-herkenning op naam / KvK van de leverancier.
 * Wijst de uitkomst de ontvangende BV zelf aan, dan is er geen voorstel.
 */
export function stelTegenpartijVoor<T extends EigenBvKandidaat>(invoer: {
  ontvangendeEntityId: string | null
  gemarkeerdeTegenpartijId: string | null
  interneLeverancierEntityId: string | null
  leverancierNaam: string | null
  leverancierKvk: string | null
  entities: readonly T[]
}): TegenpartijVoorstel<T> {
  const opId = (id: string | null): T | null =>
    id ? (invoer.entities.find((e) => e.id === id) ?? null) : null

  const kandidaten: Array<{ entity: T | null; bron: TegenpartijBron }> = [
    { entity: opId(invoer.gemarkeerdeTegenpartijId), bron: 'MARKERING' },
    { entity: opId(invoer.interneLeverancierEntityId), bron: 'INTERNE_LEVERANCIER' },
    {
      entity: herkenEigenBv(invoer.leverancierNaam, invoer.leverancierKvk, invoer.entities),
      bron: 'HERKEND',
    },
  ]
  let zelfdeBv = false
  for (const k of kandidaten) {
    if (!k.entity) continue
    if (k.entity.id === invoer.ontvangendeEntityId) {
      zelfdeBv = true
      continue
    }
    return { entity: k.entity, bron: k.bron, zelfdeBv: false }
  }
  return { entity: null, bron: null, zelfdeBv }
}

// ─── Spiegel-verkoopfactuur ──────────────────────────────────────────────────

/**
 * Vergelijk-sleutel van een factuurnummer: hoofdletters, alleen letters en
 * cijfers. Zo zijn "FA2026-S0012", "fa2026 s0012" en "FA2026S0012" hetzelfde
 * nummer. Een lege of te korte sleutel matcht nooit.
 */
export function normaliseerNummerVoorMatch(nummer: string | null | undefined): string {
  return (nummer ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '')
}

const NUMMER_SLEUTEL_MIN = 3

/** Zijn twee factuurnummers hetzelfde nummer (spelling-ongevoelig)? */
export function isZelfdeFactuurnummer(a: string | null | undefined, b: string | null | undefined): boolean {
  const ka = normaliseerNummerVoorMatch(a)
  const kb = normaliseerNummerVoorMatch(b)
  return ka.length >= NUMMER_SLEUTEL_MIN && ka === kb
}

/** Verschil in NL-kalenderdagen tussen twee instants (altijd >= 0). */
export function kalenderdagenTussen(a: Date, b: Date): number {
  const dag = (d: Date): number => {
    const { ymd } = utcToAmsterdamWallclock(d)
    return Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10)))
  }
  return Math.abs(Math.round((dag(a) - dag(b)) / DAG_MS))
}

/** De inkoopfactuur waar een spiegel bij gezocht wordt. */
export interface SpiegelBron {
  factuurNr: string
  bedragExclCenten: number
  bedragInclCenten: number
  factuurdatum: Date
}

/** Een verkoopfactuur van de verzendende BV die de spiegel zou kunnen zijn. */
export interface SpiegelKandidaat {
  id: string
  invoiceNumber: string | null
  subtotalCenten: number
  totalCenten: number
  invoiceDate: Date | null
  /** De debiteur is de ontvangende BV (interne klant, markering of herkend). */
  debiteurIsOntvanger: boolean
}

export interface SpiegelBeoordeling {
  id: string
  nummerGelijk: boolean
  bedragGelijk: boolean
  datumBinnenTolerantie: boolean
  dagenVerschil: number | null
  debiteurIsOntvanger: boolean
  sterkte: SpiegelSterkte
}

/**
 * Beoordeel één verkoopfactuur als spiegel. `null` = lijkt er op geen enkel
 * criterium op. Het bedrag is gelijk wanneer excl. OF incl. btw tot op de cent
 * overeenkomt (de twee BV's kunnen de btw net anders hebben geboekt; het
 * verschil komt dan als spiegelverschil in de status terug, nooit stil).
 */
export function beoordeelSpiegelKandidaat(
  bron: SpiegelBron,
  kandidaat: SpiegelKandidaat,
  tolerantieDagen: number = SPIEGEL_DATUM_TOLERANTIE_DAGEN,
): SpiegelBeoordeling | null {
  const nummerGelijk = isZelfdeFactuurnummer(bron.factuurNr, kandidaat.invoiceNumber)
  const bedragGelijk =
    kandidaat.subtotalCenten === bron.bedragExclCenten || kandidaat.totalCenten === bron.bedragInclCenten
  const dagenVerschil = kandidaat.invoiceDate
    ? kalenderdagenTussen(bron.factuurdatum, kandidaat.invoiceDate)
    : null
  const datumBinnenTolerantie = dagenVerschil !== null && dagenVerschil <= tolerantieDagen
  const treffers = Number(nummerGelijk) + Number(bedragGelijk) + Number(datumBinnenTolerantie)
  if (treffers === 0) return null
  // Alleen een datum-treffer is te mager voor een willekeurige factuur van die
  // BV: zonder interne debiteur is het geen kandidaat.
  if (treffers === 1 && datumBinnenTolerantie && !kandidaat.debiteurIsOntvanger) return null
  return {
    id: kandidaat.id,
    nummerGelijk,
    bedragGelijk,
    datumBinnenTolerantie,
    dagenVerschil,
    debiteurIsOntvanger: kandidaat.debiteurIsOntvanger,
    sterkte: treffers === 3 ? 'STERK' : treffers === 2 ? 'WAARSCHIJNLIJK' : 'ZWAK',
  }
}

const STERKTE_RANG: Record<SpiegelSterkte, number> = { STERK: 3, WAARSCHIJNLIJK: 2, ZWAK: 1 }

export interface SpiegelVoorstel {
  /** Kandidaten, sterkste eerst. */
  kandidaten: SpiegelBeoordeling[]
  /** De ene STERKE kandidaat; null bij geen of meer dan één. */
  eenduidig: SpiegelBeoordeling | null
}

/**
 * Stel de spiegel-verkoopfactuur voor. Sortering: sterkte, dan nummer-gelijk,
 * dan een interne debiteur, dan het kleinste datumverschil, dan id (stabiel).
 * "Eenduidig" = precies één STERKE kandidaat: alleen die mag de bulk-actie en de
 * opschoning zelf koppelen.
 */
export function stelSpiegelVoor(
  bron: SpiegelBron,
  kandidaten: readonly SpiegelKandidaat[],
  tolerantieDagen: number = SPIEGEL_DATUM_TOLERANTIE_DAGEN,
): SpiegelVoorstel {
  const beoordeeld: SpiegelBeoordeling[] = []
  for (const k of kandidaten) {
    const b = beoordeelSpiegelKandidaat(bron, k, tolerantieDagen)
    if (b) beoordeeld.push(b)
  }
  beoordeeld.sort(
    (a, b) =>
      STERKTE_RANG[b.sterkte] - STERKTE_RANG[a.sterkte] ||
      Number(b.nummerGelijk) - Number(a.nummerGelijk) ||
      Number(b.debiteurIsOntvanger) - Number(a.debiteurIsOntvanger) ||
      (a.dagenVerschil ?? Number.MAX_SAFE_INTEGER) - (b.dagenVerschil ?? Number.MAX_SAFE_INTEGER) ||
      a.id.localeCompare(b.id),
  )
  const sterk = beoordeeld.filter((b) => b.sterkte === 'STERK')
  return { kandidaten: beoordeeld, eenduidig: sterk.length === 1 ? (sterk[0] ?? null) : null }
}

// ─── Engine-doorbelastingen ──────────────────────────────────────────────────

export interface EngineKandidaat {
  id: string
  /** Doorbelast bedrag excl. btw (subtotaal + opslag), in centen. */
  bedragExclCenten: number
  /**
   * Sleutel van de correctieketen (`correctieKetenSleutels`), of leeg voor een
   * losse doorbelasting. Leden van een keten tellen als ÉÉN kandidaat met hun
   * nettobedrag en worden samen gekozen.
   */
  keten?: string | null
}

/**
 * De sleutel van de correctieketen per doorbelasting. Een correctie in de engine
 * laat het origineel staan en hangt er een credit en een nieuwe debet aan
 * (`origineelTxId`); die drie horen bij elkaar (en een correctie op de nieuwe
 * debet hangt daar weer onder). De sleutel is de bovenste voorouder die we
 * kennen; staat de ouder niet in de set (hij is bv. al via een andere geldstroom
 * afgerekend), dan is de sleutel de id van die ouder, zodat broers bij elkaar
 * blijven. Een doorbelasting zonder correctie is zijn eigen sleutel.
 */
export function correctieKetenSleutels(
  rijen: readonly { id: string; origineelTxId: string | null }[],
): Map<string, string> {
  const ouder = new Map(rijen.map((r) => [r.id, r.origineelTxId]))
  const uit = new Map<string, string>()
  for (const r of rijen) {
    let sleutel = r.id
    const gezien = new Set<string>([r.id])
    let volgende = r.origineelTxId
    while (volgende && !gezien.has(volgende)) {
      sleutel = volgende
      gezien.add(volgende)
      volgende = ouder.get(volgende) ?? null
    }
    uit.set(r.id, sleutel)
  }
  return uit
}

export type EngineVoorstelReden =
  | 'GEEN_KANDIDATEN'
  | 'TOTAAL_GELIJK'
  | 'ENKELE_GELIJK'
  | 'GEEN_EENDUIDIGE_MATCH'

export interface EngineVoorstel {
  /** De voorgeselecteerde doorbelastingen (leeg = de gebruiker kiest zelf). */
  geselecteerdIds: string[]
  reden: EngineVoorstelReden
  /** Som excl. btw van ALLE kandidaten, in centen. */
  totaalCenten: number
}

/**
 * Welke niet-afgerekende doorbelastingen van dit BV-paar en deze periode horen
 * bij de factuur? Alleen een eenduidige aansluiting wordt voorgeselecteerd:
 *   - het totaal van alle kandidaten is precies het factuurbedrag: allemaal;
 *   - anders is er precies één kandidaat met het factuurbedrag: die ene.
 * In elk ander geval kiest de gebruiker zelf (er wordt niets gegokt).
 *
 * Een correctieketen (`keten`) telt als ÉÉN kandidaat met het nettobedrag van
 * zijn leden: een gecorrigeerd origineel van 5.200 met credit -5.200 en nieuwe
 * debet 5.000 is een kandidaat van 5.000. Zo wordt nooit het losse origineel
 * gekozen terwijl de correctie open blijft staan.
 */
export function stelEngineTransactiesVoor(
  factuurExclCenten: number,
  kandidaten: readonly EngineKandidaat[],
): EngineVoorstel {
  const totaalCenten = kandidaten.reduce((s, k) => s + k.bedragExclCenten, 0)
  if (kandidaten.length === 0) return { geselecteerdIds: [], reden: 'GEEN_KANDIDATEN', totaalCenten }
  if (totaalCenten === factuurExclCenten) {
    return { geselecteerdIds: kandidaten.map((k) => k.id), reden: 'TOTAAL_GELIJK', totaalCenten }
  }
  const groepen = new Map<string, { ids: string[]; centen: number }>()
  for (const k of kandidaten) {
    const sleutel = k.keten ?? k.id
    const g = groepen.get(sleutel) ?? { ids: [], centen: 0 }
    g.ids.push(k.id)
    g.centen += k.bedragExclCenten
    groepen.set(sleutel, g)
  }
  const gelijk = [...groepen.values()].filter((g) => g.centen === factuurExclCenten)
  if (gelijk.length === 1) {
    return { geselecteerdIds: [...gelijk[0]!.ids], reden: 'ENKELE_GELIJK', totaalCenten }
  }
  return { geselecteerdIds: [], reden: 'GEEN_EENDUIDIGE_MATCH', totaalCenten }
}

// ─── Creditnota: het origineel ───────────────────────────────────────────────

export interface OrigineelBron {
  /** Het (negatieve) bedrag excl. btw van de creditnota, in centen. */
  bedragExclCenten: number
  factuurdatum: Date
  /** Referentie + omschrijving van de creditnota (waar het origineel genoemd kan staan). */
  tekst: string | null
}

export interface OrigineelKandidaat {
  geldstroomId: string
  factuurNr: string | null
  bedragExclCenten: number
  factuurdatum: Date | null
}

export interface OrigineelBeoordeling {
  geldstroomId: string
  nummerGenoemd: boolean
  bedragTegengesteld: boolean
}

export interface OrigineelVoorstel {
  kandidaten: OrigineelBeoordeling[]
  eenduidig: OrigineelBeoordeling | null
}

/** Is dit een creditnota? Een interne creditnota heeft een negatief bedrag. */
export function isInterneCredit(bedragExclCenten: number, bedragInclCenten: number): boolean {
  return bedragExclCenten < 0 || bedragInclCenten < 0
}

/**
 * Welke geldstroom crediteert deze creditnota? Kandidaten zijn positieve
 * geldstromen van hetzelfde BV-paar die niet ná de creditnota zijn gedateerd.
 * Eenduidig = precies één kandidaat waarvan het nummer in de tekst van de
 * creditnota staat; staat nergens een nummer, dan precies één kandidaat met het
 * tegengestelde bedrag.
 */
export function stelOrigineelVoor(
  credit: OrigineelBron,
  kandidaten: readonly OrigineelKandidaat[],
): OrigineelVoorstel {
  const tekstSleutel = normaliseerNummerVoorMatch(credit.tekst)
  const beoordeeld: OrigineelBeoordeling[] = []
  for (const k of kandidaten) {
    if (k.bedragExclCenten <= 0) continue
    if (k.factuurdatum && k.factuurdatum.getTime() > credit.factuurdatum.getTime() + DAG_MS) continue
    const nrSleutel = normaliseerNummerVoorMatch(k.factuurNr)
    // Minimaal 4 tekens: een kort nummer ("12") zit toevallig in elke tekst.
    const nummerGenoemd = nrSleutel.length >= 4 && tekstSleutel.includes(nrSleutel)
    const bedragTegengesteld = k.bedragExclCenten === -credit.bedragExclCenten
    if (!nummerGenoemd && !bedragTegengesteld) continue
    beoordeeld.push({ geldstroomId: k.geldstroomId, nummerGenoemd, bedragTegengesteld })
  }
  beoordeeld.sort(
    (a, b) =>
      Number(b.nummerGenoemd) - Number(a.nummerGenoemd) ||
      Number(b.bedragTegengesteld) - Number(a.bedragTegengesteld) ||
      a.geldstroomId.localeCompare(b.geldstroomId),
  )
  const genoemd = beoordeeld.filter((b) => b.nummerGenoemd)
  let eenduidig: OrigineelBeoordeling | null = null
  if (genoemd.length === 1) eenduidig = genoemd[0] ?? null
  else if (genoemd.length === 0 && beoordeeld.length === 1) eenduidig = beoordeeld[0] ?? null
  return { kandidaten: beoordeeld, eenduidig }
}

// ─── Oorsprong ───────────────────────────────────────────────────────────────

/**
 * Waar komt de interne factuur vandaan? De code van een IC-onderbouwingsfactuur
 * uit de engine wint (dan IS de factuur de engine-doorbelasting); daarna de
 * mailbox; anders is hij buiten de app gemaakt en met de hand of als bestand
 * ingevoerd.
 */
export function bepaalGeldstroomOorsprong(invoer: {
  draagtIcFactuurCode: boolean
  viaMail: boolean
}): GeldstroomOorsprong {
  if (invoer.draagtIcFactuurCode) return 'ENGINE'
  if (invoer.viaMail) return 'MAIL_IMPORT'
  return 'EXTERN_GEMAAKT'
}

// ─── Status ──────────────────────────────────────────────────────────────────

export interface GeldstroomFactuurFeit {
  /** Bedrag excl. btw in centen. */
  bedragExclCenten: number
  /** De factuur is geannuleerd, verplaatst of verwijderd. */
  vervallen: boolean
  /**
   * De BV op de factuur is niet (meer) de BV van de geldstroom: de inkoopfactuur
   * staat bij een andere ontvangende BV, of de verkoopfactuur bij een andere
   * verzendende BV. Kan ontstaan door een correctie na het koppelen.
   */
  bvWijktAf?: boolean
}

export interface GeldstroomFeiten {
  /** Kostenkant; null = geen inkoopfactuur gekoppeld. */
  inkoop: GeldstroomFactuurFeit | null
  /** Omzetkant; null = geen verkoopfactuur gekoppeld. */
  verkoop: GeldstroomFactuurFeit | null
  /**
   * Aantal afgerekende doorbelastingen en de som excl. btw van de leden die nog
   * tellen. `vervallenAantal` = gekoppelde doorbelastingen die in de engine zijn
   * ingetrokken, afgekeurd of geen bedrag meer hebben. `openCorrectie` = een
   * gekoppelde doorbelasting is in de engine gecorrigeerd en (een deel van) die
   * correctie is nog nergens afgerekend.
   */
  engine: { aantal: number; bedragExclCenten: number; vervallenAantal?: number; openCorrectie?: boolean }
  /**
   * Eerder bewust geaccepteerde verschillen (centen), of null.
   *
   * Het engine-deel hoort bij de doorbelastingen waarvoor het is gegeven. Deze
   * beoordeling vergelijkt alleen het bedrag; dat de aangeleverde acceptatie bij
   * de HUIDIGE doorbelastingen hoort, borgt de opslag: zodra de set wisselt
   * (er komt een doorbelasting bij of er gaat er een af) wist de service de
   * acceptatie. Lever hier dus nooit een acceptatie aan die voor een andere set
   * is gegeven.
   */
  geaccepteerdEngineVerschilCenten: number | null
  geaccepteerdSpiegelVerschilCenten: number | null
}

export interface GeldstroomBeoordeling {
  status: GeldstroomStatus
  /** Alleen de NIET geaccepteerde redenen; leeg tenzij status VERSCHIL. */
  redenen: GeldstroomVerschilReden[]
  /** Het bedrag van de geldstroom excl. btw: de kostenkant, anders de omzetkant. */
  bedragExclCenten: number
  /** Engine min factuur; null zonder engine-koppeling. */
  engineVerschilCenten: number | null
  /** Verkoop min inkoop; null zolang een kant ontbreekt. */
  spiegelVerschilCenten: number | null
  /** Er bestaat een verschil dat bewust geaccepteerd is (en nog exact klopt). */
  verschilGeaccepteerd: boolean
  /**
   * Per deel: is DIT verschil geaccepteerd? Een scherm dat "verschil
   * geaccepteerd" toont noemt alleen het deel waarvoor dat geldt. Na een wissel
   * van doorbelastingen kan het spiegelverschil nog geaccepteerd zijn terwijl
   * het engine-verschil weer open staat.
   */
  engineVerschilGeaccepteerd: boolean
  spiegelVerschilGeaccepteerd: boolean
}

/**
 * De status van een interne geldstroom uit de feiten. Eén regel bepaalt alles:
 * een verschil is nooit stil.
 *
 *   - een gekoppelde factuur is vervallen                       -> VERSCHIL
 *   - de BV op een gekoppelde factuur is gewijzigd              -> VERSCHIL
 *   - de verkoopfactuur heeft een ander bedrag dan de inkoop    -> VERSCHIL
 *   - de doorbelasting(en) tellen op tot een ander bedrag       -> VERSCHIL
 *   - een afgerekende doorbelasting is in de engine vervallen   -> VERSCHIL
 *   - een afgerekende doorbelasting is in de engine gecorrigeerd
 *     en de correctie is nog niet afgerekend                    -> VERSCHIL
 *   - één kant ontbreekt                                        -> OPEN
 *   - beide kanten, afgerekend met de engine                    -> AFGEREKEND
 *   - beide kanten, zonder engine                               -> GEKOPPELD
 *
 * Een bedragverschil dat bewust geaccepteerd is telt niet meer als verschil,
 * maar alleen zolang het actuele verschil EXACT het geaccepteerde bedrag is.
 * Een vervallen factuur, een gewijzigde BV, een vervallen doorbelasting en een
 * open engine-correctie zijn niet te accepteren: die los je op.
 *
 * Een geaccepteerd engine-verschil geldt bovendien alleen voor de
 * doorbelastingen waarvoor het is gegeven. Dat staat niet in deze functie maar
 * in de opslag: wisselt de set, dan wist de service de acceptatie (zie
 * `GeldstroomFeiten.geaccepteerdEngineVerschilCenten`), zodat een ander stel
 * doorbelastingen met toevallig hetzelfde verschil hier als gewoon verschil
 * binnenkomt.
 *
 * Een vervallen doorbelasting geeft alleen `ENGINE_VERVALLEN` (het bedragverschil
 * is daar het gevolg van, geen tweede oorzaak); haal hem uit de selectie en het
 * eventuele restverschil wordt als bedragverschil zichtbaar.
 */
export function beoordeelInterneGeldstroom(feiten: GeldstroomFeiten): GeldstroomBeoordeling {
  const { inkoop, verkoop, engine } = feiten
  const bedragExclCenten = inkoop?.bedragExclCenten ?? verkoop?.bedragExclCenten ?? 0
  const redenen: GeldstroomVerschilReden[] = []
  let engineVerschilGeaccepteerd = false
  let spiegelVerschilGeaccepteerd = false

  if (inkoop?.vervallen) redenen.push('INKOOPFACTUUR_VERVALLEN')
  if (verkoop?.vervallen) redenen.push('VERKOOPFACTUUR_VERVALLEN')
  if (inkoop?.bvWijktAf || verkoop?.bvWijktAf) redenen.push('BV_GEWIJZIGD')

  const spiegelVerschilCenten =
    inkoop && verkoop ? verkoop.bedragExclCenten - inkoop.bedragExclCenten : null
  if (spiegelVerschilCenten !== null && spiegelVerschilCenten !== 0) {
    if (feiten.geaccepteerdSpiegelVerschilCenten === spiegelVerschilCenten) spiegelVerschilGeaccepteerd = true
    else redenen.push('SPIEGEL_BEDRAG')
  }

  const engineVerschilCenten = engine.aantal > 0 ? engine.bedragExclCenten - bedragExclCenten : null
  if ((engine.vervallenAantal ?? 0) > 0) {
    redenen.push('ENGINE_VERVALLEN')
  } else if (engineVerschilCenten !== null && engineVerschilCenten !== 0) {
    if (feiten.geaccepteerdEngineVerschilCenten === engineVerschilCenten) engineVerschilGeaccepteerd = true
    else redenen.push('ENGINE_BEDRAG')
  }
  if (engine.openCorrectie) redenen.push('ENGINE_GECORRIGEERD')

  let status: GeldstroomStatus
  if (redenen.length > 0) status = 'VERSCHIL'
  else if (!inkoop || !verkoop) status = 'OPEN'
  else if (engine.aantal > 0) status = 'AFGEREKEND'
  else status = 'GEKOPPELD'

  return {
    status,
    redenen,
    bedragExclCenten,
    engineVerschilCenten,
    spiegelVerschilCenten,
    verschilGeaccepteerd: engineVerschilGeaccepteerd || spiegelVerschilGeaccepteerd,
    engineVerschilGeaccepteerd,
    spiegelVerschilGeaccepteerd,
  }
}

// ─── Acceptatie van een verschil ─────────────────────────────────────────────

/**
 * Zijn dit dezelfde doorbelastingen? Volgorde en dubbelen tellen niet. Een
 * geaccepteerd engine-verschil hoort bij de doorbelastingen waarvoor het is
 * gegeven: wie accepteert, accepteert exact de set die hij zag.
 */
export function isDezelfdeDoorbelastingSet(a: readonly string[], b: readonly string[]): boolean {
  const links = new Set(a)
  const rechts = new Set(b)
  if (links.size !== rechts.size) return false
  for (const id of links) if (!rechts.has(id)) return false
  return true
}

/**
 * De sleutel waarop de verschil-melding dedupliceert: binnen een ronde meldt
 * hetzelfde verschil één keer (de cron herhaalt hem niet), een ander bedrag of
 * een andere reden opnieuw.
 *
 * `ronde` = het aantal keren dat deze geldstroom Verschil VERLIET: opgelost
 * (de doorbelasting of de factuur is aangepast) of geaccepteerd. Elke keer
 * begint een nieuwe ronde. Staat de geldstroom daarna weer op Verschil, dan is
 * dat een nieuw signaal, ook wanneer bedrag en reden gelijk zijn aan een
 * verschil uit een eerdere ronde:
 *   - +200,00 geaccepteerd op doorbelasting A, daarna B gekozen die ook 200,00
 *     afwijkt (de acceptatie vervalt, het verschil staat weer open);
 *   - +200,00 op A, opgelost door C te kiezen, en weken later B met +200,00.
 * Zonder dit segment hield de melding van de vorige ronde het nieuwe signaal
 * tegen (de sleutel is uniek per ontvanger).
 */
export function geldstroomVerschilMeldSleutel(invoer: {
  redenen: readonly GeldstroomVerschilReden[]
  engineVerschilCenten: number | null
  spiegelVerschilCenten: number | null
  ronde: number
}): string {
  const basis = `${invoer.redenen.join('+')}-${invoer.engineVerschilCenten ?? 'x'}-${invoer.spiegelVerschilCenten ?? 'x'}`
  return invoer.ronde > 0 ? `${basis}-r${invoer.ronde}` : basis
}

// ─── Koppelbaarheid van een inkoopfactuur ────────────────────────────────────

/** Inkoopstatussen waarin een factuur geboekt is en dus te koppelen. */
const KOPPELBARE_INKOOP_STATUSSEN: readonly string[] = INTERNE_GELDSTROOM_KOPPELBARE_INKOOP_STATUSSEN

/**
 * Waarom een inkoopfactuur (nu) niet aan de interne geldstroom te koppelen is,
 * of null wanneer het kan. Volgorde = de meest definitieve reden eerst:
 *   - verwijderd;
 *   - als verkoopfactuur geboekt (FO-486): vanaf de inkoopzijde niet koppelbaar;
 *   - hangt al aan een geldstroom (unieke koppeling per factuur);
 *   - nog niet geboekt (klaargezet, ter controle, betwist, geannuleerd): de
 *     bedragen en de leverancier staan dan nog niet vast;
 *   - de ontvangende BV is nog niet gekozen.
 */
export function bepaalInkoopKoppelBlokkade(invoer: {
  verwijderd: boolean
  status: string
  ontvangendeEntityId: string | null
  heeftActieveGeldstroom: boolean
}): InterneGeldstroomBlokkade | null {
  if (invoer.verwijderd) return 'VERWIJDERD'
  if (invoer.status === 'VERPLAATST_NAAR_VERKOOP') return 'VERPLAATST_NAAR_VERKOOP'
  if (invoer.heeftActieveGeldstroom) return 'AL_GEKOPPELD'
  if (!KOPPELBARE_INKOOP_STATUSSEN.includes(invoer.status)) return 'NIET_GEBOEKT'
  if (!invoer.ontvangendeEntityId) return 'GEEN_ONTVANGENDE_BV'
  return null
}

// ─── Automatisch koppelen (bulk + opschoning) ────────────────────────────────

export type AutomatischKoppelOordeel =
  | { automatisch: true; actie: 'KOPPEL_BESTAANDE_SPIEGEL'; verkoopInvoiceId: string }
  | { automatisch: true; actie: 'REGISTREER_SPIEGEL' }
  | { automatisch: false; reden: GeldstroomHandmatigReden }

/**
 * Mag dit paar zonder mens gekoppeld worden? Alleen waar het EENDUIDIG is:
 *   - de verzendende BV staat vast;
 *   - bij een creditnota staat het origineel vast;
 *   - er is precies één STERKE spiegel (nummer, bedrag én datum kloppen) en de
 *     debiteur van die verkoopfactuur is herkend als de ontvangende BV. Klopt
 *     alles behalve de debiteur, dan kijkt een mens (`DEBITEUR_WIJKT_AF`): een
 *     factuur aan een externe klant mag nooit zonder controle intern worden.
 *
 * Lijkt een verkoopfactuur op NUMMER of BEDRAG maar kloppen niet alle drie, dan
 * kijkt een mens (`GEEN_STERKE_MATCH`). Een kandidaat die alleen op DATUM lijkt
 * (een andere interne factuur van hetzelfde BV-paar op dezelfde dag, met een
 * ander nummer en een ander bedrag) is geen spiegel en houdt niets tegen: twee
 * BV's sturen elkaar op de laatste dag van de maand vaak meerdere facturen.
 *
 * Is er geen kandidaat op nummer of bedrag, dan ontbreekt de spiegel. Het
 * oordeel zegt eerst wat VASTSTAAT (het nummer is bezet, of het valt in een
 * bewaakte factuurreeks en kan dus niet buiten de reeks geregistreerd worden),
 * ook in de dry-run zonder registreer-vlag; pas daarna telt de vlag.
 */
export function beoordeelAutomatischKoppelen(invoer: {
  tegenpartijBekend: boolean
  isCredit: boolean
  origineelEenduidig: boolean
  spiegel: SpiegelVoorstel
  registreerOntbrekendeSpiegels: boolean
  spiegelNummerVrij: boolean
  /** Het nummer valt in een bewaakte factuurreeks (dan niet te registreren). */
  spiegelNummerInReeks?: boolean
}): AutomatischKoppelOordeel {
  if (!invoer.tegenpartijBekend) return { automatisch: false, reden: 'GEEN_TEGENPARTIJ' }
  if (invoer.isCredit && !invoer.origineelEenduidig) {
    return { automatisch: false, reden: 'CREDIT_ZONDER_ORIGINEEL' }
  }
  if (invoer.spiegel.eenduidig) {
    if (!invoer.spiegel.eenduidig.debiteurIsOntvanger) {
      return { automatisch: false, reden: 'DEBITEUR_WIJKT_AF' }
    }
    return {
      automatisch: true,
      actie: 'KOPPEL_BESTAANDE_SPIEGEL',
      verkoopInvoiceId: invoer.spiegel.eenduidig.id,
    }
  }
  const sterk = invoer.spiegel.kandidaten.filter((k) => k.sterkte === 'STERK')
  if (sterk.length > 1) return { automatisch: false, reden: 'MEERDERE_KANDIDATEN' }
  const lijktErop = invoer.spiegel.kandidaten.some((k) => k.nummerGelijk || k.bedragGelijk)
  if (lijktErop) return { automatisch: false, reden: 'GEEN_STERKE_MATCH' }
  if (!invoer.spiegelNummerVrij) return { automatisch: false, reden: 'SPIEGELNUMMER_BEZET' }
  if (invoer.spiegelNummerInReeks) return { automatisch: false, reden: 'SPIEGELNUMMER_IN_REEKS' }
  if (!invoer.registreerOntbrekendeSpiegels) return { automatisch: false, reden: 'SPIEGEL_ONTBREEKT' }
  return { automatisch: true, actie: 'REGISTREER_SPIEGEL' }
}

// ─── Eenzijdige boekingen per BV-paar ────────────────────────────────────────

/** Sleutel van een BV-paar (verzender > ontvanger). */
export function geldstroomPaarSleutel(bronEntityId: string, ontvangendeEntityId: string): string {
  return `${bronEntityId}>${ontvangendeEntityId}`
}

export interface PaarKantTelling {
  /** Geboekte intercompany-inkoopfacturen (kostenkant) van het paar. */
  inkoopAantal: number
  inkoopCenten: number
  /** Intercompany-verkoopfacturen (omzetkant) van het paar. */
  verkoopAantal: number
  verkoopCenten: number
  /** Inkoopfacturen zonder spiegel (geen geldstroom, of geldstroom zonder verkoop). */
  inkoopZonderVerkoop: number
  /** Verkoopfacturen zonder inkoopfactuur (geen geldstroom). */
  verkoopZonderInkoop: number
}

export interface PaarSignaal {
  /** Er is iets eenzijdig geboekt of de twee kanten tellen niet gelijk op. */
  eenzijdig: boolean
  /** Verkoop min inkoop (zelfde grondslag aan beide kanten), in centen. */
  kantenVerschilCenten: number
}

/**
 * Het signaal per BV-paar: sluiten kosten (inkoop) en omzet (verkoop) aan?
 * Eenzijdig zodra er een factuur zonder tegenhanger is OF de twee kanten op een
 * ander bedrag uitkomen.
 */
export function beoordeelPaarKanten(t: PaarKantTelling): PaarSignaal {
  const kantenVerschilCenten = t.verkoopCenten - t.inkoopCenten
  return {
    eenzijdig: t.inkoopZonderVerkoop > 0 || t.verkoopZonderInkoop > 0 || kantenVerschilCenten !== 0,
    kantenVerschilCenten,
  }
}
